'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { formatFileSize, getBaseName, traceRasterToSvg } from '@/lib/browser-svg-tools';

type Variant = 'png-to-svg' | 'jpg-to-svg';
type DetailPreset = 'smooth' | 'balanced' | 'detailed';

const copy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    accept: string;
    placeholder: string;
    actionLabel: string;
    note: string;
  }
> = {
  'png-to-svg': {
    title: 'Trace a flat PNG into simple SVG markup',
    description:
      'Upload one PNG, tune the trace settings, and export a simple vector result that works best for logos, icons, and flat graphics.',
    badge: 'Trace',
    accept: 'image/png,.png',
    placeholder: 'Choose one PNG or drop it here',
    actionLabel: 'Trace PNG',
    note: 'Best for flat graphics, logos, icons, and simple shapes. This is not a full vector design replacement.',
  },
  'jpg-to-svg': {
    title: 'Trace simple JPG artwork into SVG',
    description:
      'Upload one JPG, push it toward a high-contrast trace, and export a simple SVG for logos, icons, and non-photographic artwork.',
    badge: 'Trace',
    accept: 'image/jpeg,.jpg,.jpeg',
    placeholder: 'Choose one JPG or drop it here',
    actionLabel: 'Trace JPG',
    note: 'Not photo-grade vectorization. This works best on high-contrast artwork, logos, and simple shapes.',
  },
};

export default function BrowserRasterToSvg({ variant }: { variant: Variant }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [threshold, setThreshold] = useState(variant === 'jpg-to-svg' ? 164 : 132);
  const [colorCountHint, setColorCountHint] = useState(variant === 'jpg-to-svg' ? 4 : 8);
  const [detailPreset, setDetailPreset] = useState<DetailPreset>('balanced');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [pngPreviewUrl, setPngPreviewUrl] = useState<string | null>(null);
  const [resultName, setResultName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [alphaSummary, setAlphaSummary] = useState<string>('Not traced yet');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const toolCopy = copy[variant];

  useEffect(() => {
    return () => {
      [previewUrl, resultUrl, pngPreviewUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [pngPreviewUrl, previewUrl, resultUrl]);

  function resetOutputState() {
    [resultUrl, pngPreviewUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setResultUrl(null);
    setPngPreviewUrl(null);
    setResultName(null);
    setResultSize(null);
    setAlphaSummary('Not traced yet');
  }

  function clearSource() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(null);
    setPreviewUrl(null);
  }

  function handleFileSelection(nextFile: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!nextFile) {
      clearSource();
      return;
    }

    const isAccepted =
      variant === 'png-to-svg'
        ? nextFile.type === 'image/png' || /\.png$/i.test(nextFile.name)
        : nextFile.type === 'image/jpeg' || /\.jpe?g$/i.test(nextFile.name);

    if (!isAccepted) {
      clearSource();
      setErrorMessage(
        variant === 'png-to-svg'
          ? 'Choose one PNG file for this tool.'
          : 'Choose one JPG or JPEG file for this tool.',
      );
      return;
    }

    setFile(nextFile);
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }

      return URL.createObjectURL(nextFile);
    });
  }

  async function runTrace() {
    if (!file) {
      setErrorMessage(variant === 'png-to-svg' ? 'Choose one PNG first.' : 'Choose one JPG first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another SVG trace right now.',
      );
      const result = await traceRasterToSvg({
        file,
        variant,
        threshold,
        colorCountHint,
        detailPreset,
        grayscale: variant === 'jpg-to-svg',
      });

      resetOutputState();
      setResultUrl(URL.createObjectURL(result.svgBlob));
      setPngPreviewUrl(URL.createObjectURL(result.previewPng));
      setResultName(`${getBaseName(file.name)}.svg`);
      setResultSize(result.svgBlob.size);
      setAlphaSummary(
        result.transparency.hasTransparency
          ? `${result.transparency.transparentPercent}% transparent pixels in the trace source`
          : 'No meaningful transparency in the prepared trace source',
      );
      setCreditMessage(formatToolCreditSuccess(toolCopy.actionLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The raster trace could not be created.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{toolCopy.title}</h2>
          <p>{toolCopy.description}</p>
        </div>
        <span className="tool-live-badge">{toolCopy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{file ? file.name : toolCopy.placeholder}</strong>
              <span>
                {file
                  ? `${formatFileSize(file.size)} • ${variant === 'png-to-svg' ? 'Best for flat art' : 'Best for simple non-photo artwork'}`
                  : toolCopy.note}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={toolCopy.accept}
            className="tool-live-file-input"
            onChange={(event) => handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Threshold</span>
              <input
                max="255"
                min="0"
                onChange={(event) => {
                  setThreshold(Number(event.target.value) || 0);
                  resetOutputState();
                }}
                type="range"
                value={threshold}
              />
              <small>{threshold}</small>
            </label>
            <label className="tool-live-field">
              <span>Color count hint</span>
              <input
                max="24"
                min="2"
                onChange={(event) => {
                  setColorCountHint(Number(event.target.value) || 2);
                  resetOutputState();
                }}
                type="range"
                value={colorCountHint}
              />
              <small>{colorCountHint}</small>
            </label>
          </div>

          <div className="tool-live-routes" aria-label="Trace detail">
            {[
              ['smooth', 'Smooth'],
              ['balanced', 'Balanced'],
              ['detailed', 'Detailed'],
            ].map(([id, label]) => (
              <button
                key={id}
                className={`tool-live-route ${detailPreset === id ? 'tool-live-route-active' : ''}`}
                onClick={() => {
                  setDetailPreset(id as DetailPreset);
                  resetOutputState();
                }}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="tool-live-panel-note">
            <strong>Trace note</strong>
            <span>{toolCopy.note}</span>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!file || isProcessing}
              onClick={() => void runTrace()}
              type="button"
            >
              {isProcessing ? 'Tracing…' : toolCopy.actionLabel}
            </button>
            {resultUrl && resultName ? (
              <a className="button button-ghost" download={resultName} href={resultUrl}>
                Download SVG
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Trace summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Source</strong>
              <span>{file ? formatFileSize(file.size) : 'No file loaded'}</span>
            </div>
            <div>
              <strong>SVG size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            <div>
              <strong>Detail preset</strong>
              <span>{detailPreset}</span>
            </div>
            <div>
              <strong>Alpha summary</strong>
              <span>{alphaSummary}</span>
            </div>
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Trace preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Source image</strong>
            <div className="tool-live-preview-frame">
              {previewUrl ? (
                <img alt="Source raster preview" src={previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Source preview</strong>
                  <span>Upload one source image to preview the trace input.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>Traced result</strong>
            <div className="tool-live-preview-frame">
              {pngPreviewUrl ? (
                <img alt="Traced SVG preview" src={pngPreviewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Result preview</strong>
                  <span>Run the trace to preview the generated SVG.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
